// A small key-value store on IndexedDB (falls back to memory where it isn't
// available, such as in tests or some private browsing modes).
//
// iPhones can drop the connection to IndexedDB while the app is in the
// background (opening Control Centre to switch on airplane mode is enough),
// and every read then fails until the page reloads. So a failed read or write
// reopens the database and tries once more, and everything read or written
// this session is also kept in memory, which is used if IndexedDB still fails.
const DB_NAME = 'tl-offline';
const STORE = 'kv';

let dbPromise = null;
let memory = null;          // the whole store, when IndexedDB isn't available at all
const session = new Map();  // what this session has read or written

function open() {
  if (dbPromise) return dbPromise;
  const opening = new Promise(resolve => {
    try {
      if (typeof indexedDB === 'undefined') throw new Error('no IndexedDB');
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => {
        const db = req.result;
        // Closed by the browser: open a fresh connection next time
        const forget = () => { if (dbPromise === opening) dbPromise = null; };
        db.onclose = forget;
        db.onversionchange = () => { db.close(); forget(); };
        resolve(db);
      };
      req.onerror = () => { memory = memory || new Map(); resolve(null); };
    } catch {
      memory = memory || new Map();
      resolve(null);
    }
  });
  dbPromise = opening;
  return opening;
}

function attempt(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode); // throws if the connection was lost
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

// Runs fn in a transaction, reopening the database once if it fails
async function run(mode, fn) {
  const db = await open();
  try {
    return await attempt(db, mode, fn);
  } catch {
    dbPromise = null;
    const fresh = await open();
    if (!fresh) throw new Error('IndexedDB unavailable');
    return attempt(fresh, mode, fn);
  }
}

const range = prefix => IDBKeyRange.bound(prefix, `${prefix}￿`);
const sessionEntries = prefix => [...session].filter(([k]) => k.startsWith(prefix));

export async function get(key) {
  if (!(await open())) return memory.get(key);
  try {
    const value = await run('readonly', s => s.get(key));
    if (value !== undefined) session.set(key, value);
    return value;
  } catch (err) {
    if (session.has(key)) return session.get(key);
    throw err;
  }
}

export async function set(key, value) {
  session.set(key, value);
  if (!(await open())) { memory.set(key, value); return; }
  await run('readwrite', s => s.put(value, key));
}

export async function del(key) {
  session.delete(key);
  if (!(await open())) { memory.delete(key); return; }
  await run('readwrite', s => s.delete(key));
}

export async function entries(prefix) {
  if (!(await open())) return [...memory].filter(([k]) => k.startsWith(prefix));
  try {
    const [keys, values] = await Promise.all([
      run('readonly', s => s.getAllKeys(range(prefix))),
      run('readonly', s => s.getAll(range(prefix))),
    ]);
    return keys.map((k, i) => [k, values[i]]);
  } catch (err) {
    const known = sessionEntries(prefix);
    if (known.length) return known;
    throw err;
  }
}

export async function clear(prefix) {
  sessionEntries(prefix).forEach(([k]) => session.delete(k));
  if (!(await open())) { [...memory.keys()].filter(k => k.startsWith(prefix)).forEach(k => memory.delete(k)); return; }
  await run('readwrite', s => s.delete(range(prefix)));
}
